import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import {
  Dialog,
  TextField,
  DialogTitle,
  DialogActions,
  Button,
  DialogContentText,
  DialogContent,
} from "@mui/material";
import { osName, browserName, deviceType } from "mobile-device-detect";
import ShowAlert from "./alert";
import SlackAlert from "../utils/slackAlert";
import useLanguage from "../utils/useLanguage";
import { getDomain, getDomainCode } from "../utils/utils";
import ViewsRouter from "./viewsRouter";
import Loading from "./loader";

const EXPRESS_API_URL = process.env.REACT_APP_EXPRESS_API_URL;
const ANALYTICS_URL = process.env.REACT_APP_ANALYTICS_URL;

export default function Main({ isPreview = false }) {
  const navigate = useNavigate();
  const params = useParams();
  const location = useLocation();
  const { t, setUserLanguage } = useLanguage();
  const domain = getDomainCode(process.env.REACT_APP_DOMAIN);

  const query = useMemo(() => new URLSearchParams(location.search), [location]);

  const [modal, showModal] = useState(false);
  const [data, setData] = useState(null);
  const [type, setType] = useState(null);
  const [redirect, setRedirect] = useState(false);
  const [showAlert, setAlert] = useState(false);
  const [isStatisticsUpdated, setStatisticsUpdated] = useState(false);
  const [vcfImageUrl, setVcfImageUrl] = useState(null);

  useEffect(() => {
    axios
      .get(`${EXPRESS_API_URL}/api/qr-code?uId=${params.uid}`)
      .then((result) => {
        let qrStatus = result.data?.status;

        if (!qrStatus) {
          defaultNavigate();
        }

        if (qrStatus === "ACTIVE" && !isPreview) {
          updateStatics(
            ANALYTICS_URL,
            {
              qr_code_id: result?.data?.id,
              os_name: osName,
              browserName: browserName,
              deviceType:
                typeof deviceType === "undefined" ? "desktop" : deviceType,
              utm_source: query.get("utm_source"),
              utm_medium: query.get("utm_medium"),
              utm_campaign: query.get("utm_campaign"),
            },
            () => setStatisticsUpdated(true),
            () => setStatisticsUpdated(true)
          );
        } else {
          setStatisticsUpdated(true);
        }

        redirectPreview();

        setUserLanguage(result?.data?.user_language);

        if (qrStatus === "DEACTIVATED") {
          navigate("/deactivate", {
            state: {
              is_archived: false,
              uid: "",
              unique_pricing_link: result.data.unique_pricing_link,
            },
          });
        } else if (qrStatus === "ACTIVE" || qrStatus === "DRAFT") {
          let { data: qrData, type: qrType, requires_password } = result.data;

          if (requires_password) {
            showModal(true);
            setData(qrData);
            setType(qrType);
          } else {
            setRedirect(true);
            setData(qrData);
            setType(qrType);
            if (qrType === "vcard") {
              setVcfImageUrl(qrData.vcfImage);
            }
          }
        } else {
          noDataFoundNavigation();
        }
      })
      .catch((err) => defaultNavigate(err, {}));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params.uid]);

  useEffect(() => {
    if (!vcfImageUrl) {
      return;
    }

    const fetchBase64Image = async () => {
      try {
        const base64Image = await fetch(
          `${EXPRESS_API_URL}/api/image-b64?url=${encodeURIComponent(
            vcfImageUrl
          )}`
        );
        const { base64 } = await base64Image.json();
        setData((prev) => ({ ...prev, vcfImage: base64 }));
      } catch (error) {
        console.log("Failed to fetch base64 image", error);
      }
    };

    fetchBase64Image();
  }, [vcfImageUrl]);

  const defaultNavigate = (err, uid) => {
    switch (err.response?.status) {
      case 404:
        setRedirect(true);
        setType("default");
        break;
      case 500:
        setAlert({ type: "error", msg: t("global.error.err_500") });
        slackAlert(EXPRESS_API_URL, params.uid, 500, err?.message);
        break;
      case 502:
        setAlert({ type: "error", msg: t("global.error.err_502") });
        break;
      case 0:
        setAlert({ type: "error", msg: t("global.error.no_internet") });
        break;
      default:
        noDataFoundNavigation();
        break;
    }
  };
  const noDataFoundNavigation = () => {
    navigate("/no-data");
  };

  const replaceEncodedSymbols = (obj) => {
    for (let prop in obj) {
      if (typeof obj[prop] === "string") {
        obj[prop] = obj[prop].replace(/&#039;/g, "'");
        obj[prop] = obj[prop].replace(/&lt;/g, "<");
        obj[prop] = obj[prop].replace(/&gt;/g, ">");
        obj[prop] = obj[prop].replace(/&amp;/g, "&");
      } else if (typeof obj[prop] === "object") {
        replaceEncodedSymbols(obj[prop]);
      }
    }
  };

  const redirectPage = () => {
    replaceEncodedSymbols(data);

    const showLoadingScreen =
      !isStatisticsUpdated &&
      ((type === "pdf" && data?.data?.directly_show_pdf) ||
        ["url", "whatsapp", "instagram"].includes(type));

    if (showLoadingScreen) {
      return <Loading />;
    }

    return redirect ? (
      <ViewsRouter domain={domain} type={type} data={data} />
    ) : null;
  };

  const redirectPreview = () => {
    let domainIdentification = `/${getDomain(params?.domain)}`;

    if (domainIdentification === "/q") domainIdentification = "";

    navigate(`${domainIdentification}/p/${params.uid}`);
  };

  const slackAlert = (domain, uid, errCode, reason) => {
    if (process.env.REACT_APP_CONFIG === "production") {
      SlackAlert(domain, uid, errCode, reason);
    }
  };

  return (
    <>
      {showAlert ? (
        <ShowAlert type={showAlert?.type} msg={showAlert?.msg} />
      ) : (
        <>
          {redirect ? (
            redirectPage()
          ) : (
            <PasswordModal
              open={modal}
              setRedirect={setRedirect}
              showModal={showModal}
              uid={params?.uid}
              expressApi={EXPRESS_API_URL}
              setData={setData}
              setType={setType}
            />
          )}
        </>
      )}
    </>
  );
}

const PasswordModal = ({
  open,
  setRedirect,
  showModal,
  uid,
  expressApi,
  setData,
  setType,
}) => {
  const [password, setPassword] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const { t } = useLanguage();

  const verifyPassword = async () => {
    try {
      const res = await axios.get(
        `${expressApi}/api/qr-code?uId=${uid}&password=${encodeURIComponent(
          password
        )}`
      );

      if (res.data.status === "UNAUTHORIZED") {
        setPasswordError(t("global.password.incorrect_pass"));
        return;
      }

      const qr = res?.data?.data;
      const merged = { ...(qr?.data || {}), ...(qr || {}) };
      setData(merged);
      setType(res.data?.type);
      showModal(false);
      setRedirect(true);
    } catch {
      setPasswordError(t("global.error.err_500"));
    }
  };

  useEffect(() => {
    const onKeyUp = (e) => {
      if (e.key === "Enter") verifyPassword();
    };
    window.addEventListener("keyup", onKeyUp);
    return () => window.removeEventListener("keyup", onKeyUp);
  }, [password]);

  return (
    <Dialog open={open}>
      <DialogTitle>{t("global.password.required_pass")}</DialogTitle>
      <DialogContent>
        <DialogContentText>{t("global.password.title")}</DialogContentText>
        <TextField
          autoFocus
          margin="dense"
          id="password"
          label={t("global.password.label")}
          type="password"
          fullWidth
          variant="standard"
          value={password}
          onChange={(e) => {
            setPassword(e.target.value);
            setPasswordError("");
          }}
          error={passwordError ? true : false}
          helperText={passwordError}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={verifyPassword}>
          {t("global.password.enter_btn")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

function updateStatics(domain, data, successCallback, errorCallback) {
  axios
    .post(
      `${domain}/api/analytics`,
      new URLSearchParams({
        os_name: data.os_name,
        browser_name: data.browserName,
        referrer_host: document.referrer?.split("/")[2] || "",
        referrer_path: document.referrer || "",
        device_type: data.deviceType,
        browser_language: navigator.language || navigator.userLanguage,
        qr_code_id: data.qr_code_id,
        utm_source: data.utm_source,
        utm_medium: data.utm_medium,
        utm_campaign: data.utm_campaign,
      })
    )
    .then(successCallback)
    .catch(errorCallback);
    
}